import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { OrderBundleItemModel } from '@models/types'

import type { ModuleState } from '@redux/modules/module'

const watchEntityKeys = ['orderBundleItems']

interface OrderBundleItemFilters {
  order_bundle_id: number,
}

interface Options {
  entityKey?: string,
  filters?: OrderBundleItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: {
    [key: string]: boolean,
  },
}

interface UseOrderBundleItemsPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredOrderBundleItems: OrderBundleItemModel[],
  filteredOrderBundleItemsCount: number,
  hasFilteredOrderBundleItems: boolean,
  loading?: boolean,
}

function useOrderBundleItems(options: Options = {}): UseOrderBundleItemsPayload {
  const { entityKey, filters, performHttpRequests } = options

  const { order_bundle_id: filterOrderBundleId } = filters || {}

  const {
    updatedEntities: { orderBundleItems: orderBundleItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const orderBundleItems = useSelector(reduxState => reduxState.entities.orderBundleItems)

  const filteredOrderBundleItems = useMemo(() => {
    const filtered = Object.values(orderBundleItems).filter((orderBundleItem) => {
      const { order_bundle_id } = orderBundleItem

      const matchOrderBundle = matchFilterNumber(order_bundle_id, filterOrderBundleId)

      return matchOrderBundle
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [orderBundleItemsUpdatedAt, JSON.stringify(filters)])

  const filteredOrderBundleItemsCount = filteredOrderBundleItems.length
  const hasFilteredOrderBundleItems = !!filteredOrderBundleItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrderBundleItemsCount,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
  } = loadMorePayload

  const { loading: loadingOrderBundleItems } = useReduxAction(
    'orderBundleItems',
    'loadOrderBundleItems',
    {
      entityKey,
      ...defaultRequestOptions.orderBundleItem,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2322
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderBundleItems,
    filteredOrderBundleItemsCount,
    hasFilteredOrderBundleItems,
    loading: loadingOrderBundleItems,
  }
}

export default useOrderBundleItems
